import { useMemo, useState } from 'react';
import type { PivotInputs, PivotMethod, SymbolKey, Timeframe, Trade } from '@/mocks/technical';
import { SAMPLE_TRADES, SYMBOLS, SYMBOL_DEFAULTS, TIMEFRAMES } from '@/mocks/technical';
import { computePivotLevels, computeFibLevels, findConfluence } from './calculations';

export type PivotState = {
  symbol: SymbolKey;
  inputs: Record<Timeframe, PivotInputs>;
  method: PivotMethod;
  timeframe: Timeframe;
  current: number;
};

export type FibState = {
  high: number;
  low: number;
  trend: 'up' | 'down';
  type: 'retracement' | 'extension';
  current: number;
};

export function useTechnicalStudio() {
  const [pivot, setPivot] = useState<PivotState>({
    symbol: 'eurusd',
    inputs: SYMBOL_DEFAULTS.eurusd,
    method: 'standard',
    timeframe: 'daily',
    current: SYMBOL_DEFAULTS.eurusd.daily.close,
  });
  const [fib, setFib] = useState<FibState>({
    high: 1.095,
    low: 1.08,
    trend: 'up',
    type: 'retracement',
    current: 1.0872,
  });
  const [trades, setTrades] = useState<Trade[]>(SAMPLE_TRADES);

  const symbolDef = useMemo(
    () => SYMBOLS.find((s) => s.value === pivot.symbol) ?? SYMBOLS[0],
    [pivot.symbol],
  );
  const pip = symbolDef.pip;
  const decimals = symbolDef.decimals;

  const pivotLevels = useMemo(() => {
    const { high, low, close, open } = pivot.inputs[pivot.timeframe];
    return computePivotLevels(high, low, close, open, pivot.method);
  }, [pivot.inputs, pivot.timeframe, pivot.method]);

  const mtf = useMemo(
    () =>
      TIMEFRAMES.map((tf) => {
        const { high, low, close, open } = pivot.inputs[tf.value];
        return {
          timeframe: tf.label,
          ...computePivotLevels(high, low, close, open, 'standard'),
        };
      }),
    [pivot.inputs],
  );

  const fibLevels = useMemo(
    () => computeFibLevels(fib.high, fib.low, fib.trend, fib.type),
    [fib.high, fib.low, fib.trend, fib.type],
  );

  const confluence = useMemo(
    () => findConfluence(pivotLevels, fibLevels, pip, 5),
    [pivotLevels, fibLevels, pip],
  );

  const changeSymbol = (symbol: SymbolKey) => {
    const def = SYMBOL_DEFAULTS[symbol];
    setPivot((prev) => ({
      ...prev,
      symbol,
      inputs: def,
      current: def[prev.timeframe].close,
    }));
  };

  return {
    pivot,
    setPivot,
    fib,
    setFib,
    trades,
    setTrades,
    pivotLevels,
    fibLevels,
    confluence,
    mtf,
    pip,
    decimals,
    changeSymbol,
  };
}

export type StudioApi = ReturnType<typeof useTechnicalStudio>;